// Equipment load (CHANGELOG section 7). Worn items have a weight; their sum against
// what the survivor's STR can carry decides how mobile they are. Backpack capacity
// is a different thing and stays for loot only.
//
//   loadLimit = 10 + 0.7 × STR
//   loadPct   = sum(weight of equipped items) / loadLimit × 100

import { findItemTemplate } from '../content/items';
import type { Equipment } from '../types';

export const LOAD_STATES = ['light', 'normal', 'heavy', 'overloaded', 'blocked'] as const;
export type LoadState = (typeof LOAD_STATES)[number];

export interface LoadEffects {
  /** Added to escape chance (0.05 = +5%). */
  escape: number;
  /** Added to dodge. */
  dodge: number;
  /** Initiative change: who strikes first in a round. */
  initiative: number;
  /** Longer expeditions (0.1 = +10%). */
  duration: number;
}

export const LOAD_RULES = {
  base: 10,
  perStr: 0.7,
  /** Upper bound (inclusive, in %) of each state; above the last one the survivor cannot leave. */
  upTo: { light: 50, normal: 80, heavy: 100, overloaded: 130 },
  effects: {
    light: { escape: 0.05, dodge: 0, initiative: 0, duration: 0 },
    normal: { escape: 0, dodge: 0, initiative: 0, duration: 0 },
    heavy: { escape: -0.05, dodge: -0.02, initiative: -0.05, duration: 0 },
    overloaded: { escape: -0.15, dodge: -0.06, initiative: -0.15, duration: 0.1 },
    blocked: { escape: -0.15, dodge: -0.06, initiative: -0.15, duration: 0.1 },
  } satisfies Record<LoadState, LoadEffects>,
} as const;

export interface LoadInfo {
  /** Sum of worn item weights. */
  weight: number;
  limit: number;
  pct: number;
  state: LoadState;
}

export function getLoadLimit(str: number): number {
  return LOAD_RULES.base + LOAD_RULES.perStr * Math.max(0, str);
}

/** Sum of worn weights. Broken items still weigh what they weigh. */
export function equipmentWeight(equipment: Equipment): number {
  let w = 0;
  for (const it of Object.values(equipment)) if (it) w += findItemTemplate(it.templateId)?.weight ?? 0;
  return Math.round(w * 100) / 100;
}

export function getLoadPct(equipment: Equipment, str: number): number {
  return (equipmentWeight(equipment) / getLoadLimit(str)) * 100;
}

export function getLoadState(pct: number): LoadState {
  const u = LOAD_RULES.upTo;
  if (pct <= u.light) return 'light';
  if (pct <= u.normal) return 'normal';
  if (pct <= u.heavy) return 'heavy';
  if (pct <= u.overloaded) return 'overloaded';
  return 'blocked';
}

export function loadInfo(equipment: Equipment, str: number): LoadInfo {
  const weight = equipmentWeight(equipment);
  const limit = getLoadLimit(str);
  const pct = (weight / limit) * 100;
  return { weight, limit: Math.round(limit * 10) / 10, pct: Math.round(pct * 10) / 10, state: getLoadState(pct) };
}

export function loadEffects(state: LoadState): LoadEffects {
  return LOAD_RULES.effects[state];
}

/** Extra expedition duration from the load of these stats (0.1 = +10%). */
export function loadDuration(stats: { load?: LoadInfo }): number {
  return stats.load ? loadEffects(stats.load.state).duration : 0;
}

export const LOAD_BLOCKED_MESSAGE = 'Too much gear on. Take something off before heading out.';
